import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { DockService } from './dock.service';

/**
 * Global host that wraps a region of the app and renders a panel inside it. The panel docks
 * (pushing the region's content aside), floats (bottom-right card), or fills the region
 * (full screen) — driven by {@link DockService}. The panel is a single element that is only
 * restyled per mode, so the hosted component is never re-created when the mode changes. Mount
 * once around the persistent content region (not per route) so it survives navigation.
 *
 * On small screens only full screen is offered — docked and floating collapse to it, since they
 * are visually indistinguishable at that width — so the mode switcher is hidden there.
 */
@Component({
  selector: 'ai-dock',
  imports: [NgComponentOutlet, MatButtonModule, MatIconModule, MatMenuModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dock.component.html',
  styleUrl: './dock.component.scss',
})
export class DockComponent {
  protected readonly dock = inject(DockService);

  protected readonly isMobile = toSignal(
    inject(BreakpointObserver)
      .observe(Breakpoints.Handset)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  protected readonly effectiveMode = computed(() => {
    const mode = this.dock.mode();
    if (this.isMobile()) {
      return mode === 'hidden' ? 'hidden' : 'fullscreen';
    }
    return mode;
  });

  protected readonly visible = computed(() => this.effectiveMode() !== 'hidden');
}
